import { useFetch } from "@hyper-fetch/react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Newspaper } from "lucide-react";
import { useEffect, useState } from "react";

import { CategoryChips } from "./components/category-chips";
import { FeaturedPostCard, PostCard } from "@/features/blog/components/post-card";
import { BLOG_PAGE_SIZE, blogListRequest, type BlogListFilter } from "@/features/blog/utils/blog-list-request";
import type { BlogIndex as BlogIndexData, BlogPostCard } from "@/features/blog/types";
import { GoLink, ghostButton } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** Shown before the first post goes live: what the blog will be, and the one thing to do meanwhile. */
const EmptyBlog = () => (
  <div className="relative isolate overflow-hidden rounded-[32px] border border-border bg-background px-6 py-14 text-center md:py-20">
    <div aria-hidden className="pointer-events-none absolute inset-x-8 -top-10 -z-10 grid grid-cols-3 gap-4 opacity-60 blur-[1px]">
      {[0, 1, 2].map((index) => (
        <div key={index} className={cn("h-40 rounded-[24px] bg-linear-to-b from-[var(--site-wash)] to-transparent", index === 1 && "translate-y-6")} />
      ))}
    </div>
    <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-[var(--site-wash)] text-[var(--site-ink)]">
      <Newspaper aria-hidden className="size-5" />
    </span>
    <h2 className="mx-auto mt-6 max-w-[22ch] text-[clamp(1.6rem,3vw,2.25rem)] leading-[1.08] font-medium tracking-[-0.04em] text-balance">
      {m.blog_empty_title()}
    </h2>
    <p className="mx-auto mt-4 max-w-[46ch] text-[15px] leading-relaxed text-muted-foreground">{m.blog_empty_body_coach()}</p>
    <div className="mt-8 flex justify-center">
      <GoLink to="/signup">{m.blog_empty_cta()}</GoLink>
    </div>
  </div>
);

/** A shelf or an author with nothing to read yet: say so, and point back at everything else. */
const EmptyShelf = () => (
  <div className="grid justify-items-center gap-5 rounded-[32px] border border-dashed border-border px-6 py-16 text-center">
    <p className="max-w-[40ch] text-[15px] text-muted-foreground">{m.blog_empty_shelf()}</p>
    <Link to="/blog" className={cn(ghostButton, "h-10 px-5 text-sm")}>
      {m.blog_all_posts()}
      <ArrowRight aria-hidden className="size-4" />
    </Link>
  </div>
);

/**
 * A list of the blog: the front page (the featured story big on top, the shelves, then the
 * grid), a shelf, or an author's page. The first page comes from the server so search engines read
 * it; "Show more" asks for the next ones here.
 */
export function BlogIndex({ index, filter, showChips = true }: { index: BlogIndexData; filter: BlogListFilter; showChips?: boolean }) {
  // Pages asked for after the server's first one. Each takes the next PAGE_SIZE posts of the list.
  const [pages, setPages] = useState(0);
  const [more, setMore] = useState<BlogPostCard[]>([]);
  const offset = pages * BLOG_PAGE_SIZE;
  const next = useFetch(blogListRequest(filter, offset), {
    disabled: pages === 0,
    dependencies: [offset, filter.category, filter.author, filter.lang],
  });

  // A new filter is a new list.
  useEffect(() => {
    setPages(0);
    setMore([]);
  }, [filter.category, filter.author, filter.lang]);

  useEffect(() => {
    const items = next.data?.items;
    if (pages === 0 || !items) return;
    setMore((current) => [...current, ...items.filter((item) => !current.some((seen) => seen.id === item.id))]);
  }, [next.data, pages]);

  const posts = [...index.items, ...more];
  const shown = posts.length + (index.featured ? 1 : 0);
  const hasMore = (pages + 1) * BLOG_PAGE_SIZE < index.total;
  const filtered = Boolean(filter.category || filter.author);

  if (!filtered && shown === 0) {
    return (
      <section className="pt-10 md:pt-14">
        <EmptyBlog />
      </section>
    );
  }

  return (
    <section className="pt-6 md:pt-10">
      {index.featured ? <FeaturedPostCard post={index.featured} /> : null}
      {showChips ? (
        <div className={cn(index.featured ? "mt-12 md:mt-16" : "mt-2")}>
          <CategoryChips categories={index.categories} current={filter.category} total={filtered ? undefined : index.total} />
        </div>
      ) : null}
      {posts.length === 0 ? (
        <div className="mt-8">{filtered ? <EmptyShelf /> : null}</div>
      ) : (
        <ul className="mt-8 grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((post, position) => (
            <li key={post.id}>
              <PostCard post={post} priority={!index.featured && position < 3} />
            </li>
          ))}
        </ul>
      )}
      {hasMore ? (
        <div className="mt-16 flex justify-center">
          <button
            type="button"
            disabled={next.loading}
            onClick={() => setPages((count) => count + 1)}
            className={cn(ghostButton, "h-11 px-6 text-sm disabled:opacity-60")}
          >
            {next.loading ? m.blog_loading() : m.blog_load_more()}
          </button>
        </div>
      ) : null}
    </section>
  );
}
